import {
  Button,
  PageHeader,
  Upload,
  Spin,
  Select,
} from "antd";
import { Button as MuiButton, Tooltip as MuiTooltip, Box } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  BankOutlined,
  IdcardOutlined,
  UserOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InstitutionForm } from "../elements/InstitutionForm";
import { PayeeForm } from "../elements/PayeeForm";
import { v4 } from "uuid";
import { RESULT, validateBankAccount } from "israeli-bank-validation";
import isIsraeliIdValid from "israeli-id-validator";
import { generateMasavFile } from "../functions/generateMasavFile";
import { uploadFromExcel } from "../functions/uploadFromExcel";
import { useTranslation } from "react-i18next";
import { showWarning } from "../functions/showWarning";
import { generateExcelFile } from "../functions/generateExcelFile";

import { transactionApiService, favoritesApiService, institutionApiService, savedTablesApiService } from "../functions/apiService";
import { useAuth } from "../contexts/AuthContext";

const ValidatedField = ({ text, tooltip, icon }) => {
  return (
    <MuiTooltip title={tooltip}>
      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {text}
        {icon}
      </span>
    </MuiTooltip>
  );
};

const getColumns = (t, modalRef, getUserId, setTransactions, setFavorites) => [
  {
    field: "payeeName",
    headerName: t("payee-name"),
    flex: 180,
    minWidth: 80,
    renderHeader: () => (
      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <UserOutlined style={{ color: "#FF6B35", fontSize: "16px" }} />
        {t("payee-name")}
      </span>
    ),
  },
  {
    field: "bankId",
    headerName: t("bank-id-label"),
    flex: 120,
    minWidth: 56,
    renderHeader: () => (
      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <BankOutlined style={{ color: "#FF6B35", fontSize: "16px" }} />
        {t("bank-id-label")}
      </span>
    ),
    renderCell: (params) => {
      const { bankId, branchId, accountId } = params.row;
      const result = validateBankAccount(bankId, branchId, accountId);
      let icon = <ExclamationCircleOutlined style={{ color: "#FF6B35", fontSize: "16px" }} />;
      let tooltip = t("messages-unvalidated-account");
      if (result === RESULT.NOT_VALID) {
        icon = <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />;
        tooltip = t("messages-invalid-account");
      }
      if (result === RESULT.VALID) {
        icon = <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "16px" }} />;
        tooltip = t("messages-valid-account");
      }
      return (
        <ValidatedField
          text={bankId}
          tooltip={tooltip}
          icon={icon}
        />
      );
    },
  },
  {
    field: "branchId",
    headerName: t("branch-id-label"),
    flex: 120,
    minWidth: 56,
    renderHeader: () => (
      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <BankOutlined style={{ color: "#FF6B35", fontSize: "16px" }} />
        {t("branch-id-label")}
      </span>
    ),
    renderCell: (params) => {
      const { bankId, branchId, accountId } = params.row;
      const result = validateBankAccount(bankId, branchId, accountId);
      let icon = <ExclamationCircleOutlined style={{ color: "#FF6B35", fontSize: "16px" }} />;
      let tooltip = t("messages-unvalidated-account");
      if (result === RESULT.NOT_VALID) {
        icon = <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />;
        tooltip = t("messages-invalid-account");
      }
      if (result === RESULT.VALID) {
        icon = <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "16px" }} />;
        tooltip = t("messages-valid-account");
      }
      return (
        <ValidatedField
          text={branchId}
          tooltip={tooltip}
          icon={icon}
        />
      );
    },
  },
  {
    field: "accountId",
    headerName: t("account-id-label"),
    flex: 150,
    minWidth: 64,
    renderHeader: () => (
      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <BankOutlined style={{ color: "#FF6B35", fontSize: "16px" }} />
        {t("account-id-label")}
      </span>
    ),
    renderCell: (params) => {
      const { bankId, branchId, accountId } = params.row;
      const result = validateBankAccount(bankId, branchId, accountId);
      let icon = <ExclamationCircleOutlined style={{ color: "#FF6B35", fontSize: "16px" }} />;
      let tooltip = t("messages-unvalidated-account");
      if (result === RESULT.NOT_VALID) {
        icon = <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />;
        tooltip = t("messages-invalid-account");
      }
      if (result === RESULT.VALID) {
        icon = <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "16px" }} />;
        tooltip = t("messages-valid-account");
      }
      return (
        <ValidatedField
          text={accountId}
          tooltip={tooltip}
          icon={icon}
        />
      );
    },
  },
  {
    field: "payeeId",
    headerName: t("payee-id"),
    flex: 150,
    minWidth: 64,
    renderHeader: () => (
      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <IdcardOutlined style={{ color: "#FF6B35", fontSize: "16px" }} />
        {t("payee-id")}
      </span>
    ),
    renderCell: (params) => {
      const payeeId = params.value;
      const result = isIsraeliIdValid(payeeId);
      let icon = <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />;
      let tooltip = t("messages-invalid-id");
      if (result) {
        icon = <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "16px" }} />;
        tooltip = t("messages-valid-id");
      }
      return <ValidatedField text={payeeId} tooltip={tooltip} icon={icon} />;
    },
  },
  {
    field: "payeeNumber",
    headerName: t("employee-number"),
    flex: 150,
    minWidth: 64,
  },
  {
    field: "amount",
    headerName: t("amount"),
    flex: 150,
    minWidth: 72,
    renderHeader: () => (
      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <DollarOutlined style={{ color: "#FF6B35", fontSize: "16px" }} />
        {t("amount")}
      </span>
    ),
    renderCell: (params) =>
      new Intl.NumberFormat("il-IL", {
        style: "currency",
        currency: "ILS",
      }).format(params.value),
  },
  {
    field: "actions",
    headerName: t("actions"),
    flex: 200,
    minWidth: 118,
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      const record = params.row;
      return (
        <Box sx={{ display: "flex", gap: "4px", alignItems: "center", flexWrap: "nowrap", justifyContent: "flex-end", width: "100%" }}>
          <Button
            type="text"
            onClick={() => modalRef.current.updateRow(record)}
            style={{ 
              borderRadius: "6px",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "4px 8px",
              whiteSpace: "nowrap"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255, 107, 53, 0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            <EditOutlined style={{ color: "#FF6B35", fontSize: "14px" }} />
            <span style={{ fontSize: "12px", color: "#FF6B35" }}>{t("edit-row")}</span>
          </Button>
          <Button
            type="text"
            onClick={async () => {
              const confirmed = await showWarning("about-to-delete-row");
              if (confirmed) {
                try {
                  const userId = getUserId();

                  if (!userId) {
                    console.error("No authenticated user ID available");
                    return;
                  }
                  
                  await transactionApiService.deleteTransaction(record.key);
                  setTransactions((state) =>
                    state.filter((row) => row.key !== record.key)
                  );
                  
                  const updatedFavorites = await favoritesApiService.getUserFavorites();
                  setFavorites(updatedFavorites);
                } catch (error) {
                  console.error("Failed to delete transaction:", error);
                }
              }
            }}
            style={{ 
              borderRadius: "6px",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "4px 8px",
              whiteSpace: "nowrap"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255, 77, 79, 0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            <DeleteOutlined style={{ color: "#ff4d4f", fontSize: "14px" }} />
            <span style={{ fontSize: "12px", color: "#ff4d4f" }}>{t("delete-row")}</span>
          </Button>
        </Box>
      );
    },
  },
  {
    field: "addRowButton",
    headerName: "",
    flex: 220,
    minWidth: 100,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderHeader: () => (
      <MuiButton
        onClick={() => modalRef.current.addRow()}
        variant="contained"
        size="small"
        sx={{
          minWidth: { xs: "110px", sm: "160px" },
          maxWidth: "100%",
          padding: "0 8px",
          borderRadius: "999px",
          background: "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
          boxShadow: "0 3px 10px rgba(82, 196, 26, 0.35)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          fontSize: "12px",
          fontWeight: "500",
          textTransform: "none",
          "&:hover": {
            background: "linear-gradient(135deg, #389e0d 0%, #52c41a 100%)",
            boxShadow: "0 4px 12px rgba(82, 196, 26, 0.45)",
          },
        }}
      >
        <PlusOutlined style={{ fontSize: "14px" }} />
        <span>{t("add-new-transaction")}</span>
      </MuiButton>
    ),
    renderCell: () => null,
  },
];

// Remove getTransactionFromStorage and all localStorage for transactions

const getDefaultInstitution = () => {
  return {
    institutionId: "",
    institutionName: "",
    sendingInstitutionId: "",
    serialNumber: "001"
  };
};

export const OnlineConvertor = () => {
  const { t } = useTranslation("online-convertor");
  const { getUserId, isAuthenticated } = useAuth();
  const modalRef = useRef();
  const [transactions, setTransactions] = useState([]); // Start with empty array
  const [institutionDetails, setInstitutionDetails] = useState(
    getDefaultInstitution()
  );
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [termsChecked, setTermsChecked] = useState(false);
  const [savedTables, setSavedTables] = useState([]);
  
  
  // Initialize data from API
  const initializeData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Don't proceed if not authenticated
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      
      const userId = getUserId();

      if (!userId) {
        console.error("No authenticated user ID available");
        setLoading(false);
        return;
      }
      
      // Fetch saved tables first
      let savedTablesData = [];
      try {
        savedTablesData = await savedTablesApiService.getSavedTables();
        setSavedTables(savedTablesData);
      } catch (error) {
        console.error("Failed to fetch saved tables:", error);
        setSavedTables([]);
      }
      
      // Fetch transactions from API
      const apiTransactions = await transactionApiService.syncWithApi();

      // תמיד נטען רק את הטרנזקציות האקטואליות מה־API
      // טבלאות שמורות יוצגו רק כשמשתמש בוחר אותן ידנית מה־Select
      setTransactions(apiTransactions);
      
      // Fetch favorites
      const apiFavorites = await favoritesApiService.getUserFavorites();
      setFavorites(apiFavorites);
      
      // Fetch institution data from API
      const apiInstitution = await institutionApiService.getInstitutionByUserId();
      if (apiInstitution) {
        setInstitutionDetails({
          institutionId: apiInstitution.institution_id || "",
          institutionName: apiInstitution.institution_name || "",
          sendingInstitutionId: apiInstitution.sending_institution_id || "",
          serialNumber: apiInstitution.serial_number || "001"
        });
      }

      // Check if user needs to accept terms (only once)
      if (!termsChecked) {
        setTermsChecked(true);
        const termsAgreedValue = apiInstitution?.terms_agreed;
        // `terms_agreed` used to be boolean; now it's a date/timestamp (string/Date) or null.
        const termsAgreed =
          termsAgreedValue === true ||
          (typeof termsAgreedValue === "string"
            ? termsAgreedValue.trim() !== "" && termsAgreedValue.trim().toLowerCase() !== "false"
            : Boolean(termsAgreedValue));
        if (!termsAgreed) {
          // Show terms agreement modal - user must agree to continue
          const { showTermsAgreement } = await import("../functions/showTermsAgreement");
          const agreed = await showTermsAgreement(getUserId());
          
          if (agreed) {
            // User agreed - reload institution data to get updated terms_agreed status
            const updatedInstitution = await institutionApiService.getInstitutionByUserId();
            if (updatedInstitution) {
              setInstitutionDetails({
                institutionId: updatedInstitution.institution_id || "",
                institutionName: updatedInstitution.institution_name || "",
                sendingInstitutionId: updatedInstitution.sending_institution_id || "",
                serialNumber: updatedInstitution.serial_number || "001"
              });
            }
          } else {
            // User didn't agree - modal stays open, don't allow using the system
            // The modal will block the interface
            return;
          }
        }
      }
      
    } catch (error) {
      console.error("Failed to initialize data:", error);
      setTransactions([]); // No fallback to localStorage
    } finally {
      setLoading(false);
    }
  }, [getUserId, isAuthenticated, termsChecked]);

  // Initialize on component mount
  useEffect(() => {
    initializeData();
  }, [initializeData]);



  const onFormFinishClick = useCallback(async (fields, isEdit) => {
    try {
      const userId = getUserId();

      if (!userId) {
        console.error("No authenticated user ID available");
        return;
      }

      // Create key for new records, use isEdit as key when editing
      const key = isEdit || v4();

      // Create payload
      const payload = {
        bankId: fields.bankId,
        branchId: fields.branchId,
        accountId: fields.accountId,
        payeeName: fields.payeeName,
        payeeId: fields.payeeId,
        payeeNumber: fields.payeeNumber,
        amount: fields.amount,
        key,
      };

      if (isEdit) {
        // Update existing transaction
        await transactionApiService.updateTransaction(payload);
        setTransactions((prev) => 
          prev.map((t) => (t.key === key ? payload : t))
        );
      } else {
        // Insert new transaction
        await transactionApiService.insertTransaction(payload);
        setTransactions((prev) => [payload, ...prev]);
      }

      // Add to favorites
      await favoritesApiService.addOrIncrementFavorite({
        bank_id: payload.bankId,
        branch_id: payload.branchId,
        account_id: payload.accountId,
        payee_name: payload.payeeName,
        payee_id: payload.payeeId,
        payee_number: payload.payeeNumber
      });

      // Refresh favorites list to get updated rank
      const updatedFavorites = await favoritesApiService.getUserFavorites();
      setFavorites(updatedFavorites);

    } catch (error) {
      console.error("❌ Error during save:", error);
      // Show error to user (you might want to add a notification system)
    }
  }, [getUserId]);

  const columns = useMemo(
    () => getColumns(t, modalRef, getUserId, setTransactions, setFavorites),
    [t, modalRef, getUserId, setTransactions, setFavorites]
  );

  // Prepare data for DataGrid - need id field
  const rows = useMemo(() => {
    return transactions.map((trans) => ({
      id: trans.key,
      ...trans,
    }));
  }, [transactions]);

  const totalAmount = useMemo(() => {
    return transactions.reduce((sum, trans) => sum + (trans.amount || 0), 0);
  }, [transactions]);

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        overflow: "hidden",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <PageHeader
        className="online-convertor-page-header"
        onBack={null}
        title={
          <span style={{ color: "#fff", fontWeight: "600" }}>
            {t("title")}
          </span>
        }
        subTitle={t("sub-title") ? <span style={{ color: "rgba(255, 255, 255, 0.85)" }}>{t("sub-title")}</span> : null}
        style={{
          background: "linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)",
          borderRadius: "8px 8px 0 0",
          marginBottom: "0",
          padding: "6px 14px",
          flexShrink: 0,
          boxShadow: "0 4px 12px rgba(255, 107, 53, 0.3)",
        }}
      />
      <div style={{ flexShrink: 0 }}>
        <InstitutionForm
          onDataChange={setInstitutionDetails}
          institutionDetails={institutionDetails}
          userId={getUserId()}
        />
      </div>
      {/* Action Buttons Area - Left side above table */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap",
          marginBottom: "8px",
          justifyContent: "flex-start",
          flexShrink: 0,
        }}
      >
        {savedTables.length > 0 && (
          <Select
            placeholder={t("previous-transactions")}
            style={{ 
              minWidth: "180px", 
              height: "36px",
            }}
            size="middle"
            className="saved-tables-select"
            dropdownStyle={{ zIndex: 1050 }}
            onChange={async (value) => {
              try {
                // value can be id (from Lambda) or combination of table_name and created_at (fallback)
                const selectedTable = savedTables.find(t => {
                  return t.id === value || 
                         `${t.table_name}_${t.created_at}` === value || 
                         t.table_name === value;
                });
                if (selectedTable) {
                  // Restore institution details
                  if (selectedTable.institution) {
                    setInstitutionDetails({
                      institutionId: selectedTable.institution.institution_id || "",
                      institutionName: selectedTable.institution.institution_name || "",
                      sendingInstitutionId: selectedTable.institution.sending_institution_id || "",
                      serialNumber: selectedTable.institution.serial_number || "001"
                    });
                  }
                  
                  // Add transactions from saved table to existing transactions
                  // Map saved table transactions to the correct format
                  const newTransactions = (selectedTable.transactions && Array.isArray(selectedTable.transactions) && selectedTable.transactions.length > 0)
                    ? selectedTable.transactions.map(t => ({
                        key: t.key || v4(),
                        bankId: t.bank_id,
                        branchId: t.branch_id,
                        accountId: t.account_id,
                        payeeName: t.payee_name,
                        payeeId: t.payee_id,
                        payeeNumber: t.payee_number,
                        amount: t.amount
                      }))
                    : []; // Empty array if no transactions
                  
                  // Just add to local state - don't insert to API (they already exist in the saved table)
                  // Filter out duplicates by key to avoid adding the same transaction twice
                  setTransactions((prev) => {
                    const existingKeys = new Set(prev.map(t => t.key));
                    const uniqueNewTransactions = newTransactions.filter(t => !existingKeys.has(t.key));
                    return [...prev, ...uniqueNewTransactions];
                  });
                  
                  // Refresh favorites
                  const updatedFavorites = await favoritesApiService.getUserFavorites();
                  setFavorites(updatedFavorites);
                }
              } catch (error) {
                console.error("Failed to load saved table:", error);
              }
            }}
            value={null}
            allowClear
          >
            {savedTables.map((table) => {
              // Use id if available (from Lambda), otherwise use combination of table_name and created_at
              const tableKey = table.id || `${table.table_name}_${table.created_at}`;
              return (
                <Select.Option key={tableKey} value={tableKey}>
                  {table.table_name || table.name || "Unnamed Table"}
                </Select.Option>
              );
            })}
          </Select>
        )}
        <Upload
          name="file"
          accept=".xlsx"
          showUploadList={false}
          beforeUpload={async (file) => {
            try {
              const userId = getUserId();

              if (!userId) {
                console.error("No authenticated user ID available");
                return false;
              }
              
              const { transactions: importedTransactions, institution } = await uploadFromExcel(
                file,
                t
              );
              
              // Add keys to imported transactions
              const processedTransactions = importedTransactions.map(t => ({
                ...t,
                key: t.key || v4()
              }));
              
              // Insert all transactions to API
              for (const transaction of processedTransactions) {
                await transactionApiService.insertTransaction(transaction);
              }
              
              // Update local state
              setTransactions((old) => [...old, ...processedTransactions]);
              
              // Refresh favorites list to get updated ranks
              const updatedFavorites = await favoritesApiService.getUserFavorites();
              setFavorites(updatedFavorites);
              
              if (institution) setInstitutionDetails(institution);
              
              return false;
            } catch (error) {
              console.error("Failed to import Excel file:", error);
              return false;
            }
          }}
        >
          <MuiButton
            sx={{ 
              height: "36px",
              minWidth: "160px",
              padding: "0 24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              fontSize: "14px",
              fontWeight: "600",
              textTransform: "none",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)",
              boxShadow: "0 4px 12px rgba(255, 107, 53, 0.3)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                background: "linear-gradient(135deg, #E55A2B 0%, #FF6B35 100%)",
                boxShadow: "0 6px 16px rgba(255, 107, 53, 0.4)",
                transform: "translateY(-2px)",
              },
              "&:active": {
                transform: "translateY(0)",
              },
            }}
            variant="contained"
            size="large"
          >
            {t("import-excel")}
            <FileExcelOutlined style={{ fontSize: "18px" }} />
          </MuiButton>
        </Upload>
        <MuiButton
          sx={{ 
            height: "36px",
            minWidth: "140px",
            padding: "0 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            fontSize: "14px",
            fontWeight: "600",
            textTransform: "none",
            borderRadius: "10px",
            background: "linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)",
            boxShadow: "0 4px 12px rgba(255, 77, 79, 0.3)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              background: "linear-gradient(135deg, #cf1322 0%, #ff4d4f 100%)",
              boxShadow: "0 6px 16px rgba(255, 77, 79, 0.4)",
              transform: "translateY(-2px)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
          }}
          variant="contained"
          size="large"
          onClick={async () => {
            const confirmed = await showWarning("about-to-delete-all");
            if (confirmed) {
              try {
                const userId = getUserId();

                if (!userId) {
                  console.error("No authenticated user ID available");
                  return;
                }
                
                await transactionApiService.deleteAllTransactions();
                setTransactions([]);
                
                // Refresh favorites list to get updated ranks
                const updatedFavorites = await favoritesApiService.getUserFavorites();
                setFavorites(updatedFavorites);
              } catch (error) {
                console.error("Failed to delete all transactions:", error);
                // Show error to user
              }
            }
          }}
        >
          {t("delete-all-button")}
          <DeleteOutlined style={{ fontSize: "18px" }} />
        </MuiButton>
        <MuiButton
          sx={{ 
            height: "36px",
            minWidth: "180px",
            padding: "0 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            fontSize: "14px",
            fontWeight: "600",
            textTransform: "none",
            borderRadius: "10px",
            background: "linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)",
            boxShadow: "0 4px 12px rgba(255, 107, 53, 0.3)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              background: "linear-gradient(135deg, #E55A2B 0%, #FF6B35 100%)",
              boxShadow: "0 6px 16px rgba(255, 107, 53, 0.4)",
              transform: "translateY(-2px)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
          }}
          variant="contained"
          size="large"
          onClick={() => generateExcelFile(transactions, t)}
        >
          {t("download-excel-button")}
          <FileExcelOutlined style={{ fontSize: "18px" }} />
        </MuiButton>
      </div>
      
      {/* Example File Link */}
      <div style={{ marginBottom: "8px", flexShrink: 0 }}>
        <a 
          style={{ 
            color: "#FF6B35", 
            textDecoration: "none",
            fontWeight: "500",
            fontSize: "14px",
            transition: "color 0.3s ease",
            display: "inline-flex",
            alignItems: "center",
            gap: "4px"
          }} 
          download 
          href="/example.xlsx"
          onMouseEnter={(e) => e.target.style.color = "#E55A2B"}
          onMouseLeave={(e) => e.target.style.color = "#FF6B35"}
        >
          {t("example-file-link")}
        </a>
      </div>
      
      {/* DataGrid Container — flex:1 fills remaining viewport; scroll stays inside grid */}
      <div style={{ 
        width: "100%",
        flex: 1,
        minHeight: 0,
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
      }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, minHeight: "220px" }}>
            <Spin size="large" />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center", 
            flex: 1,
            minHeight: "220px",
            padding: "16px" 
          }}>
            <MuiButton
              onClick={() => modalRef.current.addRow()}
              variant="contained"
              size="medium"
              sx={{ 
                height: "40px",
                minWidth: "160px",
                padding: "0 24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                fontSize: "15px",
                fontWeight: "600",
                textTransform: "none",
                borderRadius: "10px",
                background: "linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)",
                boxShadow: "0 4px 12px rgba(255, 107, 53, 0.3)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  background: "linear-gradient(135deg, #E55A2B 0%, #FF6B35 100%)",
                  boxShadow: "0 6px 16px rgba(255, 107, 53, 0.4)",
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <PlusOutlined style={{ fontSize: "18px" }} />
              {t("add-new-transaction")}
            </MuiButton>
          </Box>
        ) : (
          <>
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
                maxWidth: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <DataGrid
                rows={rows}
                columns={columns}
                loading={loading}
                getRowId={(row) => row.id}
                disableRowSelectionOnClick
                disableColumnMenu
                hideFooter
                rowHeight={46}
                headerHeight={60}
                autoHeight={false}
                sx={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  fontSize: "0.875rem",
                  /* headerHeight prop must match real header row — else first row overlaps headers */
                  "& .MuiDataGrid-main": {
                    overflowX: "hidden",
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    overflowX: "hidden !important",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    minHeight: "60px !important",
                    maxHeight: "none",
                    backgroundColor: "#fafafa",
                    borderBottom: "2px solid #FF6B35",
                    fontWeight: 600,
                  },
                  "& .MuiDataGrid-columnHeaderRow": {
                    height: "60px !important",
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #f0f0f0",
                    userSelect: "text",
                    cursor: "text",
                    fontSize: "0.875rem",
                    display: "flex",
                    alignItems: "center",
                    lineHeight: 1.35,
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                  },
                  "& .MuiDataGrid-cell[data-field='actions']": {
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  },
                  "& .MuiDataGrid-columnHeader": {
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiDataGrid-columnHeader[data-field='actions']": {
                    justifyContent: "flex-end",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "rgba(255, 107, 53, 0.05)",
                  },
                  "& .MuiDataGrid-row": {
                    userSelect: "none",
                    cursor: "default",
                  },
                }}
              />
            </div>
            {/* Summary Row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "10px 12px",
                backgroundColor: "#fafafa",
                borderTop: "2px solid #FF6B35",
                gap: "12px",
                width: "100%",
                flexShrink: 0,
                flexWrap: "wrap",
                justifyContent: { xs: "center", sm: "flex-start" },
              }}
            >
              <Box sx={{ fontWeight: "bold", fontSize: "13px", minWidth: { xs: "auto", sm: "160px" } }}>
                {t("total")}
              </Box>
              <Box sx={{ 
                fontWeight: "bold", 
                fontSize: "13px", 
                textAlign: "left",
                minWidth: { xs: "auto", sm: "130px" },
              }}>
                {new Intl.NumberFormat("il-IL", {
                  style: "currency",
                  currency: "ILS",
                }).format(totalAmount)}
              </Box>
              <Box sx={{ 
                flex: 1,
                minWidth: 0,
                display: "flex", 
                justifyContent: "flex-end",
              }}>
                <MuiButton
                  sx={{ 
                    height: { xs: "40px", sm: "36px" },
                    minWidth: { xs: "100%", sm: "160px" },
                    padding: "0 20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    fontSize: "13px",
                    fontWeight: "600",
                    textTransform: "none",
                    borderRadius: "8px",
                    background: "linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)",
                    boxShadow: "0 3px 8px rgba(255, 107, 53, 0.3)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #E55A2B 0%, #FF6B35 100%)",
                      boxShadow: "0 4px 12px rgba(255, 107, 53, 0.4)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                  }}
                  variant="contained"
                  size="medium"
                  onClick={async () => {
                    await generateMasavFile(
                      { institution:institutionDetails, transactions }, 
                      t,
                      async (updatedTables) => {
                        setSavedTables(updatedTables);
                      }
                    );
                    if (typeof window.refreshMasavUsage === "function") {
                      try {
                        await window.refreshMasavUsage();
                      } catch (error) {
                        console.error("Failed to refresh usage info:", error);
                      }
                    }
                  }}
                >
                  {t("download-file-button")}
                  <FileTextOutlined style={{ fontSize: "16px" }} />
                </MuiButton>
              </Box>
            </Box>
          </>
        )}
      </div>
      
      <PayeeForm ref={modalRef} onFormFinishClick={onFormFinishClick} favorites={favorites} />

    </div>
  );
};
